import React, { useState, useEffect } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { apiCall, AuthError } from '../utils/apiUtils';
import { useMsal } from "@azure/msal-react";
import { useBrandVoice } from '../contexts/BrandVoiceContext';
import SettingsPanel from './SettingsPanel';
import PhaseProgress from './PhaseProgress';

const TREND_TRACKER_AREA = 'google-trend';
const TREND_PHASES = [
  'Fetching trending topic...',
  'Analyzing results...',
  'Showing latest trend...',
];

// Move placeholderWords outside component as a constant since it never changes
const PLACEHOLDER_WORDS = [
  "Elektrisch rijden",
  "Duurzame techniek",
  "Onderwijs",
  "Nieuwbouw projecten",
  "Vintage fashion",
  "Lead generatie",
  "Interieurstyling advies",
  "Huizenprijzen",
  "AI tools",
  "Zakelijke events",
  "EV-oplossingen",
  "Schone energie",
  "Klantenwerving",
  "Automatisering",
  "Internet of Things",
  "Content creatie",
  "Supply chain",
  "Carbon footprint"
];

export default function Trend({ change, onClick }) {
  const [message, setMessage] = useState('');
  const [inputError, setInputError] = useState(false);
  const [noResultsError, setNoResultsError] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');
  const [timeFrameIndex, setTimeFrameIndex] = useState(4);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [isAnimating, setIsAnimating] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Trend search is open to anonymous visitors — the API layer
  // transparently attaches an anonymous session token when no MSAL
  // account is present, so no local auth gate is needed here.
  const { geo } = useBrandVoice();
  const { instance } = useMsal();

  const timeFrames = [
    { value: 'now 1-d', label: 'Last Day' },
    { value: 'now 7-d', label: 'Last 7 Days' },
    { value: 'today 1-m', label: 'Last Month' },
    { value: 'today 3-m', label: 'Last 3 Months' },
    { value: 'today 12-m', label: 'Last 12 Months' }
  ];

  // Remove the useMemo since placeholderWords is now a constant

  useEffect(() => {
    if (!isAnimating) {
      setAnimatedPlaceholder('');
      return;
    }

    let currentWordIndex = 0;
    let currentIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;
    let deletingSpeed = 50;
    let pauseDuration = 1000;
    let timeoutId;

    const animate = () => {
      if (!isAnimating) return;

      const currentWord = PLACEHOLDER_WORDS[currentWordIndex];
      
      if (isDeleting) {
        setAnimatedPlaceholder(currentWord.slice(0, currentIndex - 1));
        currentIndex--;
        
        if (currentIndex === 0) {
          isDeleting = false;
          currentWordIndex = (currentWordIndex + 1) % PLACEHOLDER_WORDS.length;
          timeoutId = setTimeout(animate, typingSpeed);
        } else {
          timeoutId = setTimeout(animate, deletingSpeed);
        }
      } else {
        setAnimatedPlaceholder(currentWord.slice(0, currentIndex + 1));
        currentIndex++;
        
        if (currentIndex === currentWord.length) {
          isDeleting = true;
          timeoutId = setTimeout(animate, pauseDuration);
        } else {
          timeoutId = setTimeout(animate, typingSpeed);
        }
      }
    };

    animate();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAnimating]); // Now ESLint won't complain about missing dependencies

  const handleChange = (event) => {
    setMessage(event.target.value);
    if (inputError) setInputError(false); // Reset error state on change
    if (noResultsError) setNoResultsError(false); // Reset no results error
    if (authError) {
      setAuthError(false);
      setAuthErrorMessage('');
    }
  };

  const handleTimeFrameChange = (event) => {
    setTimeFrameIndex(parseInt(event.target.value));
  };

  const handleLogin = () => {
    instance.loginRedirect();
  };

  async function handleApiCall() {
    if (!message.trim()) {
      setInputError(true);
      return;
    }

    // Reset error states for a fresh search. Anonymous visitors are
    // allowed through — apiCall will transparently mint/attach an
    // anonymous session token. We only fall back to a sign-in prompt
    // if the server explicitly rejects the anonymous path (e.g. the
    // per-IP rate limit has been exhausted).
    setInputError(false);
    setNoResultsError(false);
    setAuthError(false);
    setAuthErrorMessage('');
    setIsLoading(true);
    
    try {
      const responseData = await trackPromise(
        apiCall(`/google-trend?key_word=${message}&time_frame=${timeFrames[timeFrameIndex].value}&geo=${geo}&cat=0&gprop=`, null, {
          method: 'GET'
        }),
        TREND_TRACKER_AREA
      );
      
      // Check if responseData is empty, null, or undefined
      if (!responseData || (Array.isArray(responseData) && responseData.length === 0)) {
        setNoResultsError(true);
        return;
      }
      
      onClick({ responseData, originalKeyword: message });
      change();
    } catch (error) {
      console.error(error);
      // Rate-limit / forbidden paths surface as 403 or a custom anon
      // error code; everything else we still let the user retry without
      // interrupting them with a sign-in CTA.
      const anonCode = error?.originalError?.response?.data?.code;
      const isAnonIpLimit = anonCode === 'ANON_IP_LIMIT_REACHED';
      const isAuthFailure =
        error?.type === AuthError.NO_ACCOUNT ||
        error?.type === AuthError.TOKEN_EXPIRED ||
        isAnonIpLimit;
      if (isAuthFailure) {
        setAuthError(true);
        setAuthErrorMessage(
          isAnonIpLimit
            ? "You've reached the free-trial limit on this network. Sign in to keep creating blogs."
            : 'Sign in to keep creating blogs.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApiCall();
    }
  };

  const handleFocus = (event) => {
    setIsAnimating(false);
    setAnimatedPlaceholder('');
    event.target.placeholder = '';
    setInputError(false);
    setNoResultsError(false);
    setAuthError(false);
    setAuthErrorMessage('');
  };

  const handleBlur = (event) => {
    if (!event.target.value.trim()) {
      setMessage('');
      setInputError(true);
      setIsAnimating(true);
    }
  };

  return (
    <div className='Trend'>
      <h1>Find trending topics</h1>
      <h2>think fast!</h2>
      {isLoading ? (
        <PhaseProgress phases={TREND_PHASES} isActive={isLoading} />
      ) : (
        <>
          <input
            type="text"
            id="message"
            className={inputError || noResultsError || authError ? 'input-error' : ''}
            placeholder={inputError ? "Please enter a subject" : animatedPlaceholder}
            onChange={handleChange}
            onKeyDown={handleKeyPress}
            value={message}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {authError && (
            <div className="auth-error-message">
              <p>{authErrorMessage || 'Sign in to keep creating blogs.'}</p>
              <button className="login-button" onClick={handleLogin}>
                Sign In
              </button>
            </div>
          )}
          {noResultsError && (
            <div className="no-results-message">
              No results found. Try a longer time period or a different region below.
            </div>
          )}
          <button
            className={
              inputError || noResultsError || authError
                ? 'button-error'
                : message.trim()
                  ? 'button-active'
                  : ''
            }
            onClick={() => handleApiCall()}
          >
            Generate
          </button>
          <div className="time-frame-container">
            <input
              type="range"
              min="0"
              max={timeFrames.length - 1}
              value={timeFrameIndex}
              onChange={handleTimeFrameChange}
              className="time-frame-slider"
            />
            <div className="time-frame-label">{timeFrames[timeFrameIndex].label}</div>
          </div>
          <SettingsPanel />
        </>
      )}
    </div>
  );
}


